import { isDefined } from "@clipboard-health/util-ts";
import { Stack } from "@mui/material";
import { Rating, Text } from "@src/appV2/redesign/system/ui-components";
import { type WorkplaceCommentAttributesType } from "@src/appV2/redesign/Workplace/Review/api/useWorkplaceComments";
import pluralize from "pluralize";

import { useGetReviewTag } from "../hooks/useGetReviewTag";
import { ReviewTagPill } from "./ReviewTagPill";

interface ReviewContextPillsProps {
  shiftPeriod?: WorkplaceCommentAttributesType["shiftPeriod"];
  patientsPerNurse?: string;
  rating?: number;
  reviewTag?: WorkplaceCommentAttributesType["reviewTag"];
}

/**
 * Formats the patient-per-nurse ratio for display in review cards.
 * Handles both exact numbers ("5") and ranges ("1-5").
 */
function formatPatientRatio(patientsPerNurse: string | undefined): string | undefined {
  const trimmedValue = patientsPerNurse?.trim();

  if (!trimmedValue) {
    return undefined;
  }

  // Check if it's a range (contains hyphen)
  if (trimmedValue.includes("-")) {
    const parts = trimmedValue.split("-").map((part) => part.trim());
    // eslint-disable-next-line prefer-destructuring
    const start = parts[0];
    // eslint-disable-next-line prefer-destructuring
    const end = parts[1];

    if (!start || !end || Number.isNaN(Number(start)) || Number.isNaN(Number(end))) {
      return undefined;
    }

    return `${start} - ${end} ${pluralize("patient", Number(end))}`;
  }

  // Single number
  const count = Number.parseInt(trimmedValue, 10);

  if (Number.isNaN(count)) {
    return undefined;
  }

  return `${count} ${pluralize("patient", count)}`;
}

/**
 * Displays review context info: star rating, shift type pill, and patient ratio.
 * Items are separated by dots and only shown when the corresponding data is available.
 */
export function ReviewContextPills(props: ReviewContextPillsProps) {
  const { shiftPeriod, patientsPerNurse, rating, reviewTag } = props;

  const patientRatioText = formatPatientRatio(patientsPerNurse);
  const reviewTagContent = useGetReviewTag({
    shiftPeriod,
    reviewTag,
  });

  const items: React.ReactNode[] = [];

  if (isDefined(rating)) {
    items.push(<Rating key="rating" readOnly value={rating} size="small" />);
  }

  if (isDefined(reviewTagContent)) {
    items.push(<ReviewTagPill key="reviewTag" content={reviewTagContent} />);
  }

  if (isDefined(patientRatioText)) {
    items.push(
      <Text key="patientRatio" variant="body2" color={(theme) => theme.palette.text.primary}>
        {patientRatioText}
      </Text>
    );
  }

  if (items.length === 0) {
    return null;
  }

  return (
    <Stack
      direction="row"
      spacing={3}
      alignItems="center"
      sx={{ flexWrap: "wrap" }}
      divider={
        <Text variant="body2" color={(theme) => theme.palette.text.secondary}>
          •
        </Text>
      }
    >
      {items}
    </Stack>
  );
}

// Export for testing
export { formatPatientRatio };
